/**
 * WallSketch annotation geometry properties — registration and getters.
 *
 * Properties registered here:
 * - skeleton_nodes (JSON, PROJECT_GLOBAL) — full nodes array
 * - skeleton_edges (JSON, PROJECT_GLOBAL) — full edges array
 * - skeleton_metadata (JSON, PROJECT_GLOBAL) — metadata object
 * - skeleton_wall_regions (JSON, PROJECT_GLOBAL) — outer wall outlines + cavities
 *
 * Floor height is intentionally NOT a wall-sketch property — a wall sketch
 * inherits the height range of the floor group it belongs to (see
 * PointoramaWallSketch.getFloorHeight / getCeilingHeight).
 */

import { PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { TypedJsonProperty } from '../../reactive/TypedJsonProperty';

/**
 * One bound (top or bottom) of a skeleton element's vertical extent.
 *
 * Resolves to an absolute world-Z at render time via `resolveSkeletonBound`.
 * Storage is always metric; the UI converts to feet for display when the
 * user's unit preference is imperial.
 */
export interface SkeletonHeightBound {
  /**
   * `'default'` = this floor's own reference height (the floor plane for the
   * bottom bound, the next floor above / this floor's ceiling for the top
   * bound). `{ targetFloorId }` = the floor with that group identifier.
   */
  mode: 'default' | { targetFloorId: string };
  /** Extra height in METERS added on top of the resolved reference. May be negative. */
  offset: number;
}

/**
 * Per-element height constraint for skeleton nodes and edges: an independent
 * upper (`top`) and lower (`bottom`) bound. The element extrudes from the
 * resolved bottom Z to the resolved top Z.
 */
export interface SkeletonHeightConstraint {
  top: SkeletonHeightBound;
  bottom: SkeletonHeightBound;
}

/** Default top bound: next floor above / this floor's ceiling, no offset. */
export const DEFAULT_SKELETON_TOP_BOUND: SkeletonHeightBound = { mode: 'default', offset: 0 };
/** Default bottom bound: this floor's own floor plane, no offset. */
export const DEFAULT_SKELETON_BOTTOM_BOUND: SkeletonHeightBound = { mode: 'default', offset: 0 };

/** Default height constraint applied when an element has no explicit `height`. */
export const DEFAULT_SKELETON_HEIGHT_CONSTRAINT: SkeletonHeightConstraint = {
  top: DEFAULT_SKELETON_TOP_BOUND,
  bottom: DEFAULT_SKELETON_BOTTOM_BOUND,
};

/** WallSketch node data */
export interface SkeletonNodeData {
  /** Opaque unique identifier (UUID). Used by every reference field (`edgeIds`, `startNodeId`/`endNodeId`, `roomIds`). */
  id: string;
  /** Human/display label. Not used for identity. */
  name: string;
  position: { x: number; y: number; z: number };
  roomIds: string[];
  edgeIds: string[];
  /** Per-node height constraint. Missing = {@link DEFAULT_SKELETON_HEIGHT_CONSTRAINT}. Independent of incident edges. */
  height?: SkeletonHeightConstraint;
  metadata?: string | null;
}

/** Wall type classification for skeleton edges */
export type SkeletonWallType = 'INNER' | 'OUTER' | 'BLOCK';

/**
 * Structural role of a skeleton edge beyond its wall classification.
 * `'OUTLINE'` marks an edge as part of the floor's measured outer-wall
 * boundary (the merged former OUTLINE annotation): a zero-thickness boundary
 * line (`leftSize = rightSize = 0`, `wallType: 'OUTER'`) that is never
 * skinned and whose closed cycles form the floor's outline rings (see
 * skeleton/outlineRings.ts in the client). Absent = a normal wall edge.
 * NOTE: `wallType: 'OUTER'` alone is NOT a discriminator — the skeleton
 * generator also emits OUTER-typed centerline walls.
 */
export type SkeletonEdgeRole = 'OUTLINE';

/** WallSketch edge data */
export interface SkeletonEdgeData {
  /** Opaque unique identifier (UUID). Referenced by `SkeletonNodeData.edgeIds` and by `OPENING` annotations via the `opening_skeleton_edge_id` property. */
  id: string;
  /** Human/display label. Not used for identity. */
  name: string;
  startNodeId: string;
  endNodeId: string;
  roomIds: string[];
  /** Wall thickness on the left side (perpendicular to edge direction) */
  leftSize: number;
  /** Wall thickness on the right side (perpendicular to edge direction) */
  rightSize: number;
  /** Classification of the wall type */
  wallType: SkeletonWallType;
  /** Per-edge height constraint. Missing = {@link DEFAULT_SKELETON_HEIGHT_CONSTRAINT}. Independent of incident nodes. */
  height?: SkeletonHeightConstraint;
  metadata?: string | null;
  /**
   * Circular-arc curvature of this edge, DXF-style: bulge = tan(θ/4) where θ
   * is the arc's included angle. Positive bows the wall to the LEFT of the
   * start→end direction (the `leftSize` side). 0, absent, or |bulge| below the
   * straightness epsilon means a straight edge — fully backward compatible
   * with existing persisted data. Sagitta = (chordLength/2)·|bulge|.
   */
  bulge?: number;
  /**
   * Structural role of the edge. Absent = normal wall; `'OUTLINE'` = merged
   * outer-wall boundary line (see {@link SkeletonEdgeRole}). Optional field on
   * a JSON-blob property — fully backward compatible, like `bulge`.
   */
  role?: SkeletonEdgeRole;
}

/**
 * Persisted wall-region data: a 2D polygon component representing one
 * connected piece of the outer wall outline plus any interior cavities
 * (fireplaces, courtyards, light wells). Generated by booleon-unioning
 * inflated room polygons; recomputed on user trigger only (not auto).
 *
 * Rings are stored as plain {x, y} objects so they round-trip through JSON.
 * Outer rings are CCW; holes are CW (polygon-clipping convention).
 */
export interface WallRegionData {
  outerRing: { x: number; y: number }[];
  holes: { x: number; y: number }[][];
}

export function registerWallSketchGeometryProperties() {
  if (!propertyRegistry.isRegistered('skeleton_nodes')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_nodes',
      name: 'Nodes',
      valueType: PropertyValueType.JSON,
      defaultValue: '[]',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_edges')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_edges',
      name: 'Edges',
      valueType: PropertyValueType.JSON,
      defaultValue: '[]',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_metadata')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_metadata',
      name: 'Metadata',
      valueType: PropertyValueType.JSON,
      defaultValue: 'null',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('skeleton_wall_regions')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'skeleton_wall_regions',
      name: 'Wall Regions',
      valueType: PropertyValueType.JSON,
      defaultValue: '[]',
      annotationTypes: [],
    });
  }
}

registerWallSketchGeometryProperties();

const nodesCache = new Map<string, TypedJsonProperty<SkeletonNodeData[]>>();

export function getWallSketchNodesProperty(annotationId: string): TypedJsonProperty<SkeletonNodeData[]> {
  const cached = nodesCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('skeleton_nodes', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedJsonProperty<SkeletonNodeData[]>(raw);
  nodesCache.set(annotationId, typed);
  return typed;
}

const edgesCache = new Map<string, TypedJsonProperty<SkeletonEdgeData[]>>();

export function getWallSketchEdgesProperty(annotationId: string): TypedJsonProperty<SkeletonEdgeData[]> {
  const cached = edgesCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('skeleton_edges', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedJsonProperty<SkeletonEdgeData[]>(raw);
  edgesCache.set(annotationId, typed);
  return typed;
}

const metadataCache = new Map<string, TypedJsonProperty<unknown>>();

export function getWallSketchMetadataProperty(annotationId: string): TypedJsonProperty<unknown> {
  const cached = metadataCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('skeleton_metadata', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedJsonProperty<unknown>(raw);
  metadataCache.set(annotationId, typed);
  return typed;
}

const wallRegionsCache = new Map<string, TypedJsonProperty<WallRegionData[]>>();

export function getWallSketchWallRegionsProperty(annotationId: string): TypedJsonProperty<WallRegionData[]> {
  const cached = wallRegionsCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('skeleton_wall_regions', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedJsonProperty<WallRegionData[]>(raw);
  wallRegionsCache.set(annotationId, typed);
  return typed;
}
